import {
  Card,
  Grid,
  Divider,
  Switch,
  TextField,
  FormControlLabel,
  Icon,
  Chip,
} from '@mui/material';
import { Controller, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import MDBox from 'components/MDBox';
import MDButton from 'components/MDButton';
import MDTypography from 'components/MDTypography';
import axios from 'axios';
import { useEffect } from 'react';
import { Loading, Notify } from 'notiflix';
import * as yup from 'yup';
import { ENDPOINT } from 'constants';
import { useSelector } from 'react-redux';
import DayPicker from './DayPicker';

const globalReminderSchema = yup.object().shape({
  enableReminders: yup.boolean().required(),
  daysBeforeDue: yup.array().of(yup.number()),
  onDueDate: yup.boolean(),
  daysAfterDue: yup.array().of(yup.number()),
  autoCancelAfterDays: yup.number().nullable(),
  cartReminderDays: yup.array().of(yup.number()),
  reminderTime: yup.string(),
  channels: yup.array().of(yup.string()),
});

// Section wrapper — consistent icon + title + description across the page, so every
// group of settings reads the same way at a glance.
const SettingsSection = ({ icon, title, description, children }) => (
  <MDBox mb={4}>
    <MDBox display="flex" alignItems="flex-start" gap={1.5} mb={2}>
      <MDBox
        width="36px"
        height="36px"
        borderRadius="lg"
        bgColor="info"
        display="flex"
        justifyContent="center"
        alignItems="center"
        flexShrink={0}
        sx={{ opacity: 0.9 }}
      >
        <Icon fontSize="small" sx={{ color: '#fff' }}>{icon}</Icon>
      </MDBox>
      <MDBox>
        <MDTypography variant="button" fontWeight="bold" display="block">
          {title}
        </MDTypography>
        {description && (
          <MDTypography variant="caption" color="secondary">
            {description}
          </MDTypography>
        )}
      </MDBox>
    </MDBox>
    <MDBox pl={{ xs: 0, sm: 6.5 }}>{children}</MDBox>
  </MDBox>
);

// Pure content — no DashboardLayout/Navbar of its own. Embedded as a tab in the
// Reminder Settings hub (src/layouts/reminderSettings/index.jsx).
const GlobalSettingsView = () => {
  const { user } = useSelector((state) => state.auth);

  const {
    control,
    handleSubmit,
    reset,
    watch,
    formState: { isSubmitting, isDirty },
  } = useForm({
    resolver: yupResolver(globalReminderSchema),
    defaultValues: {
      enableReminders: true,
      daysBeforeDue: [],
      onDueDate: false,
      daysAfterDue: [],
      autoCancelAfterDays: 0,
      cartReminderDays: [],
      reminderTime: '10:00:00',
      channels: ['email'],
    },
  });

  const enableReminders = watch('enableReminders');
  const smsEnabled = watch('channels')?.includes('sms');
  const autoCancelAfterDays = watch('autoCancelAfterDays');

  useEffect(() => {
    fetchSettings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchSettings = async () => {
    try {
      Loading.circle('Loading settings...');
      const { data } = await axios.get(`${ENDPOINT}api/v1/admin/reminder/global`, {
        headers: { Authorization: `Bearer ${user?.accessToken}` },
      });
      const s = data.data;
      reset({
        ...s,
        daysBeforeDue: s.daysBeforeDue ?? [],
        daysAfterDue: s.daysAfterDue ?? [],
        cartReminderDays: s.cartReminderDays ?? [],
        channels: s.channels ?? ['email'],
      });
    } catch (error) {
      console.error(error);
      Notify.failure('Failed to fetch reminder settings');
    } finally {
      Loading.remove();
    }
  };

  const handleSave = async (data) => {
    try {
      Loading.circle('Updating settings...');
      await axios.put(`${ENDPOINT}api/v1/admin/reminder/global`, data, {
        headers: { Authorization: `Bearer ${user?.accessToken}` },
      });
      Notify.success('Global reminder settings updated successfully');
      reset(data);
    } catch (error) {
      console.error(error);
      Notify.failure('Failed to update reminder settings');
    } finally {
      Loading.remove();
    }
  };

  return (
    <Grid container spacing={3}>
      <Grid item xs={12} lg={12}>
        <Card sx={{ p: { xs: 2.5, md: 4 } }}>
          <MDBox display="flex" justifyContent="space-between" alignItems="flex-start" flexWrap="wrap" gap={2} mb={1}>
            <MDBox>
              <MDTypography variant="h6" fontWeight="bold">Global Reminder Settings</MDTypography>
              <MDTypography variant="caption" color="secondary">
                These defaults apply to every user unless overridden individually.
              </MDTypography>
            </MDBox>
            {!enableReminders && (
              <Chip label="Reminders are OFF globally" color="error" size="small" />
            )}
          </MDBox>
          <Divider sx={{ my: 3 }} />

          <form onSubmit={handleSubmit(handleSave)}>
            {/* ── A1. Reminder mode ── */}
            <SettingsSection
              icon="power_settings_new"
              title="Reminder mode"
              description="The master switch — turning this off stops every reminder, for every user, immediately."
            >
              <Controller
                name="enableReminders"
                control={control}
                render={({ field }) => (
                  <FormControlLabel
                    control={
                      <Switch checked={field.value} onChange={(e) => field.onChange(e.target.checked)} />
                    }
                    label={
                      <MDTypography variant="button" fontWeight="medium">
                        Enable reminders globally
                      </MDTypography>
                    }
                  />
                )}
              />
            </SettingsSection>

            <Divider sx={{ my: 3 }} />

            {/* ── A2. Frequency setup ── */}
            <SettingsSection
              icon="schedule"
              title="Frequency setup"
              description="When reminders go out, relative to a renewal, due date, overdue payment, or abandoned cart."
            >
              <DayPicker
                control={control}
                name="daysBeforeDue"
                label="Days before due (renewal reminders)"
                hint="e.g. 7, 3, 1 days before a subscription renews"
              />

              <Controller
                name="onDueDate"
                control={control}
                render={({ field }) => (
                  <FormControlLabel
                    control={
                      <Switch checked={field.value} onChange={(e) => field.onChange(e.target.checked)} />
                    }
                    label="Also send a reminder on the due date itself"
                    sx={{ mb: 2, display: 'block' }}
                  />
                )}
              />

              <DayPicker
                control={control}
                name="daysAfterDue"
                label="Days after due (overdue reminders)"
                hint="e.g. 1, 3, 7, 14 days after the due date has passed"
              />

              <DayPicker
                control={control}
                name="cartReminderDays"
                label="Abandoned cart reminder days"
                hint="Days since a cart was last touched, with no checkout"
              />
            </SettingsSection>

            <Divider sx={{ my: 3 }} />

            {/* ── A3. Channels ── */}
            <SettingsSection
              icon="forum"
              title="Channels to use"
              description="Every reminder is sent by email. SMS is optional and uses the SMS content configured on each template."
            >
              <MDBox display="flex" alignItems="center" gap={1} mb={1.5}>
                <Chip icon={<Icon>mail</Icon>} label="Email — always on" color="info" size="small" />
              </MDBox>
              <Controller
                name="channels"
                control={control}
                render={({ field }) => (
                  <FormControlLabel
                    control={
                      <Switch
                        checked={field.value?.includes('sms')}
                        onChange={(e) =>
                          field.onChange(e.target.checked ? ['email', 'sms'] : ['email'])
                        }
                      />
                    }
                    label="Also send reminders via SMS"
                  />
                )}
              />
              {smsEnabled && (
                <MDTypography variant="caption" color="text" display="block" mt={0.5}>
                  Only sent for events whose template has SMS content configured — see Email Templates.
                </MDTypography>
              )}
            </SettingsSection>

            <Divider sx={{ my: 3 }} />

            {/* ── A4. Auto-cancellation ── */}
            <SettingsSection
              icon="block"
              title="Auto-cancellation"
              description="Automatically place unpaid orders on hold once they've been overdue this many days."
            >
              <Controller
                name="autoCancelAfterDays"
                control={control}
                render={({ field }) => (
                  <TextField
                    label="Cancel unpaid orders after (days)"
                    type="number"
                    inputProps={{ min: 0 }}
                    {...field}
                    fullWidth
                    sx={{ maxWidth: 320 }}
                    helperText={
                      Number(autoCancelAfterDays) === 0
                        ? 'Set to 0 to disable auto-cancellation entirely'
                        : `Orders overdue ${autoCancelAfterDays}+ days are placed on hold`
                    }
                  />
                )}
              />
            </SettingsSection>

            <Divider sx={{ my: 3 }} />

            {/* ── A5. Reminder time ── */}
            <SettingsSection
              icon="alarm"
              title="Reminder time"
              description="What time of day the reminder job runs, in the server's timezone."
            >
              <Controller
                name="reminderTime"
                control={control}
                render={({ field }) => (
                  <TextField
                    label="Daily send time"
                    type="time"
                    value={(field.value || '10:00:00').slice(0, 5)}
                    onChange={(e) => field.onChange(e.target.value ? `${e.target.value}:00` : '')}
                    fullWidth
                    sx={{ maxWidth: 320 }}
                    InputLabelProps={{ shrink: true }}
                    inputProps={{ step: 60 }}
                    helperText="Server timezone — must match the cron job schedule"
                  />
                )}
              />
            </SettingsSection>

            <Divider sx={{ my: 3 }} />

            <MDBox display="flex" justifyContent="flex-end" gap={2}>
              {isDirty && (
                <MDTypography variant="caption" color="warning" alignSelf="center">
                  Unsaved changes
                </MDTypography>
              )}
              <MDButton type="submit" variant="gradient" color="info" disabled={isSubmitting}>
                {isSubmitting ? 'Saving...' : 'Save Global Settings'}
              </MDButton>
            </MDBox>
          </form>
        </Card>
      </Grid>

    </Grid>
  );
};

export default GlobalSettingsView;
